/**
 * The surfaces you are not looking at, kept per tab.
 *
 * The URL carries only the surface on screen (see `navUrl.ts`), which is what stopped a Setup
 * address bar from naming Cards' deck and Read's log. That trim would otherwise cost something
 * real: leave Cards mid-search, go to Setup, refresh — and the search is gone, because the URL was
 * the only thing holding it. This is where it lives instead.
 *
 * **`sessionStorage`, not `localStorage`.** It is per tab, which is the honest scope for "the
 * surfaces this window has open": two Cards tabs with different searches must not overwrite each
 * other. `logTabs.ts` uses `localStorage` for the reading strip and does have exactly that wart —
 * worth not copying.
 *
 * **This is a convenience, never a source of truth.** A missing, malformed or half-written store
 * degrades to the defaults; nothing here may throw, because it would take the whole app down on
 * boot over a preference. Anything that would *hurt* to lose is server state, and anything that
 * must survive being pasted into another browser is in the URL.
 */

import {
  DEFAULT_NAV_STATE,
  parseNavUrl,
  surfacesInSearch,
  type CardsUrlState,
  type GroupsUrlState,
  type NavState,
  type ReadUrlState,
  type SearchUrlState,
  type SetupUrlState,
} from "./navUrl";

export const STORAGE_KEY = "nihongonator:nav";

/** Exactly the surface slices, and never `tab` — where you are is the URL's to say. */
export interface StoredSurfaces {
  cards?: Partial<CardsUrlState>;
  read?: Partial<Omit<ReadUrlState, "moving">>;
  search?: Partial<SearchUrlState>;
  setup?: Partial<SetupUrlState>;
  /** The open group and its filters — **never `drill`**, on Review's reasoning: restoring one would
   *  start a run on a rail click. */
  groups?: Partial<Omit<GroupsUrlState, "drill">>;
}

/**
 * Merge what the URL says with what this tab remembers.
 *
 * **Per surface, the URL wins if it speaks at all.** A link naming `cards.deck` — hand-edited, or
 * sent by someone else — must beat whatever this tab happens to have stored, or following a link
 * would silently show your own filters instead of the ones you were sent. Only the surfaces the URL
 * is silent about are filled in from the store.
 *
 * `moving` is never restored from either side: a refresh landing you in the library with a sentence
 * still half-moved is worse than one that forgets the move.
 */
export function restoreNavState(search: string, stored: StoredSurfaces): NavState {
  const fromUrl = parseNavUrl(search);
  const spoken = surfacesInSearch(search);

  return {
    ...fromUrl,
    cards: spoken.has("cards") ? fromUrl.cards : { ...fromUrl.cards, ...(stored.cards ?? {}) },
    read: spoken.has("read")
      ? fromUrl.read
      : { ...fromUrl.read, ...(stored.read ?? {}), moving: null },
    search: spoken.has("search") ? fromUrl.search : { ...fromUrl.search, ...(stored.search ?? {}) },
    setup: spoken.has("setup") ? fromUrl.setup : { ...fromUrl.setup, ...(stored.setup ?? {}) },
    groups: spoken.has("groups")
      ? fromUrl.groups
      : { ...fromUrl.groups, ...(stored.groups ?? {}), drill: null },
  };
}

/** What of a state is worth remembering for the next load of this tab. */
export function storableSurfaces(state: NavState): StoredSurfaces {
  return {
    cards: state.cards,
    read: { view: state.read.view, log: state.read.log },
    search: state.search,
    setup: state.setup,
    groups: { group: state.groups.group, cards: state.groups.cards },
  };
}

export function loadStoredSurfaces(
  storage: Storage | undefined = safeStorage(),
): StoredSurfaces {
  try {
    const raw = storage?.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as StoredSurfaces;
    if (!parsed || typeof parsed !== "object") return {};
    // Each slice is checked on its own, so one malformed surface cannot cost the other two. The
    // values inside are not: `restoreNavState` spreads them over a full default, and every consumer
    // already tolerates a field it does not recognise.
    return {
      cards: isObject(parsed.cards) ? parsed.cards : undefined,
      read: isObject(parsed.read) ? parsed.read : undefined,
      search: isObject(parsed.search) ? parsed.search : undefined,
      setup: isObject(parsed.setup) ? parsed.setup : undefined,
      groups: isObject(parsed.groups) ? parsed.groups : undefined,
    };
  } catch {
    return {};
  }
}

export function saveStoredSurfaces(
  state: NavState,
  storage: Storage | undefined = safeStorage(),
): void {
  try {
    storage?.setItem(STORAGE_KEY, JSON.stringify(storableSurfaces(state)));
  } catch {
    /* private mode, or a full quota. A lost filter set is not worth failing a render over. */
  }
}

function isObject(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

/** Absent under SSR and in a suite with no DOM, and throwing outright in some privacy modes. */
function safeStorage(): Storage | undefined {
  try {
    return typeof window === "undefined" ? undefined : window.sessionStorage;
  } catch {
    return undefined;
  }
}

/** The empty state, for a caller that has nothing stored. */
export const NO_STORED_SURFACES: StoredSurfaces = {};

/** Re-exported so a caller needs one import to get a starting state. */
export { DEFAULT_NAV_STATE };
